<template>
  <modal v-bind="$attrs" v-on="$listeners" :errors="errors">
    <template v-slot:title>
      <h1>
        {{title}}
      </h1>
    </template>
    <template v-slot:body>
      <el-form class="auth-form" :model="form" :rules="rules" ref="form">
        <div class="form-row">
          <elsa-input name="first_name" :label="$t('organization_profile.edit_organization_modal.learner.first_name')" class="first-name" :placeholder="$t('organization_profile.edit_organization_modal.learner.first_name')" v-model.lazy="form.first_name">
            {{ $t('organization_profile.edit_organization_modal.city_field.label') }}
          </elsa-input>
          <elsa-input name="last_name" :label="$t('organization_profile.edit_organization_modal.learner.last_name')" class="last-name" :placeholder="$t('organization_profile.edit_organization_modal.learner.last_name')" v-model.lazy="form.last_name">
            {{ $t('organization_profile.edit_organization_modal.state_field.label') }}
          </elsa-input>
        </div>
        <div v-if="canEditRole" class="form-row row-roles">
          <label class="role-label"> Roles </label>
          <el-checkbox-group class="elsa-checkbox-group group-roles" v-model.lazy="form.roles">
            <el-checkbox
              v-for="({value, label}, idx) in availableRoles"
              :key="idx"
              class="elsa-checkbox"
              :label="value"
            >
              {{$labelRole(currentOrganizationType, value)}}
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </el-form>
    </template>
    <template v-slot:footer>
      <span class="dialog-footer elsa-button-footer">
          <tertiary-button @click.native="handleCancelClick">
            {{ $t('common.cancel') }}
          </tertiary-button>
          <primary-button disabled @click.native="handleUpdate" ref="submit" type="submit">
            {{ $t('common.confirm') }}
          </primary-button>
        </span>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/common/Modal'
import PrimaryButton from '@/components/common/buttons/PrimaryButton'
import TertiaryButton from '@/components/common/buttons/TertiaryButton'
import ElsaInput from '@/components/common/Input'
import { handleSubmitButtonState, isPropEmpty, handleFormValidation } from '@/helpers/formValidationHelper'
import { selectUserRoles } from '@/constants/userRoles'
import { mapGetters } from 'vuex'

export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    ElsaInput
  },
  props: {
    organizationType: {
      type: String
    },
    title: {
      default: "",
      type: String
    },
    userEdited: {
      id: '',
      first_name: '',
      last_name: '',
      roles: []
    },
  },
  data() {
    return {
      errors: {},
      form: {
        roles: ['']
      },
      rules: {
        first_name: [
          {
            type: 'string',
            message: this.$t('organization_profile.edit_organization_modal.learner.first_name_validation_msg'),
            trigger: 'blur',
            required: true
          }
        ],
        last_name: [
          {
            type: 'string',
            message: this.$t('organization_profile.edit_organization_modal.learner.last_name_validation_msg'),
            trigger: 'blur',
            required: true
          }
        ]
      }
    }
  },
  methods: {
    handleUpdate(e) {
      e.preventDefault()
      return handleFormValidation(this.$refs.form, this.updateUsers, this.handleError)
    },
    updateUsers() {
      this.$emit('updateLearner', {
          id: this.userEdited.id,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          roles: this.form.roles,
        });
      this.resetModal();
    },
    handleCancelClick() {
      this.resetModal()
    },
    onFormChange() {
      if (this.$refs.submit) {
        handleSubmitButtonState(this.$refs.submit.$el, (this.userEditedStr != this.formUserEditedStr))
      }
    },
    handleError(error) {
      this.errors = error
    },
    resetModal() {
      this.errors = {}
      this.$refs.form.resetFields()
      if (this.$refs.submit) {
        handleSubmitButtonState(this.$refs.submit.$el, true)
      }

      this.$emit('closed')
    }
  },
  computed: {
    ...mapGetters('organization', [
      'currentOrganizationType',
      'isOrganizationAdmin',
      'isGeneralAdmin'
    ]),
    ...mapGetters('user', ['isElsaAdmin']),
    canEditRole() {
      return this.isElsaAdmin;
    },
    userEditedStr() {
      let ret = ''
      if (this.userEdited) {
        ret = `${this.userEdited.first_name}|${this.userEdited.last_name}${this.userEdited.roles.join("")}`
      }
      return ret
    },
    formUserEditedStr() {
      let ret = ''
      if (this.form) {
        ret = `${this.form.first_name}|${this.form.last_name}${this.form.roles.join("")}`
      }
      return ret
    },
    availableRoles () { return selectUserRoles },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        return this.onFormChange()
      }
    },
    "form.roles"(newV, oldV) {
      if(newV.includes("admin") && newV.includes("associate_admin")) {
        if(oldV.includes("admin")) {
          this.form["roles"] = this.form["roles"].filter(item => item != "admin");
        } else {
          this.form["roles"] = this.form["roles"].filter(item => item != "associate_admin");
        }
      }
      if(newV.length == 0) {
        this.form["roles"] = ["student"]
      }
    },
    userEditedStr: {
      handler(oldV, newV) {
        if (oldV !== newV && this.userEdited && this.userEdited.id) {
          this.form = {
            first_name: this.userEdited.first_name,
            last_name: this.userEdited.last_name,
            roles: this.userEdited.roles,
          }
        }
      }
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-dialog.elsa-modal {
  width: 788px;
}
.elsa-flat-button.el-button {
  padding: 0;
  margin: 0;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  text-transform: capitalize;
}
.auth-form {
  .form-row {
    display: flex;
    .first-name,
    .last-name {
      width: 50%;
    }
    .first-name {
      margin-right: 30px;
    }
  }
  .row-roles {
    .role-label {
      font-size: 16px;
      font-weight: 500;
    }
    flex-direction: column;
    .group-roles {
      flex-direction: row;
      .elsa-checkbox {
        margin-right: 79px;
      }
    }
  }
}
</style>
<style lang="scss">
.elsa-button-footer {
  padding: 0px !important;
}
</style>
>
